/* Gameness bulk order form block */
(function () {
  'use strict';

  if (customElements.get('bulk-order-form')) return;

  const selectors = {
    row: '[data-bulk-row]',
    filter: '[data-bulk-filter]',
    clear: '[data-bulk-clear]',
    toggle: '[data-bulk-toggle]',
    toggleText: '[data-bulk-toggle-text]',
    quantityField: '[data-quantity-field]',
    minus: '[data-bulk-minus]',
    plus: '[data-bulk-plus]',
    submit: '[data-bulk-submit]',
    total: '[data-bulk-total]',
    subtotal: '[data-bulk-subtotal]',
    summary: '[data-bulk-summary]',
    results: '[data-bulk-results]',
    empty: '[data-bulk-empty]',
    error: '[data-bulk-error]',
  };

  const classes = {
    hidden: 'hidden',
    loading: 'is-loading',
    selected: 'is-selected',
    active: 'is-active',
    unavailable: 'bulk-order-form__row--unavailable',
    extra: 'bulk-order-form__row--extra',
  };

  class BulkOrderForm extends HTMLElement {
    connectedCallback() {
      this.cacheElements();
      this.expanded = this.toggleButton?.getAttribute('aria-expanded') === 'true';
      this.isSubmitting = false;

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onInput = this.onInput.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onInput);

      this.updateFilters();
      this.refreshSelection();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('input', this.onInput);
    }

    cacheElements() {
      this.rows = Array.from(this.querySelectorAll(selectors.row));
      this.filters = Array.from(this.querySelectorAll(selectors.filter));
      this.clearButton = this.querySelector(selectors.clear);
      this.toggleButton = this.querySelector(selectors.toggle);
      this.toggleText = this.querySelector(selectors.toggleText);
      this.submitButton = this.querySelector(selectors.submit);
      this.totalElement = this.querySelector(selectors.total);
      this.subtotalElement = this.querySelector(selectors.subtotal);
      this.summaryElement = this.querySelector(selectors.summary);
      this.resultsElement = this.querySelector(selectors.results);
      this.emptyElement = this.querySelector(selectors.empty);
      this.errorElement = this.querySelector(selectors.error);
    }

    onClick(event) {
      const clearButton = event.target.closest(selectors.clear);
      if (clearButton) {
        event.preventDefault();
        this.clearFilters();
        return;
      }

      const toggleButton = event.target.closest(selectors.toggle);
      if (toggleButton) {
        event.preventDefault();
        this.toggleRows();
        return;
      }

      const minusButton = event.target.closest(selectors.minus);
      if (minusButton) {
        event.preventDefault();
        this.changeQuantity(minusButton, -1);
        return;
      }

      const plusButton = event.target.closest(selectors.plus);
      if (plusButton) {
        event.preventDefault();
        this.changeQuantity(plusButton, 1);
        return;
      }

      const submitButton = event.target.closest(selectors.submit);
      if (submitButton) {
        this.onSubmit(event);
      }
    }

    onChange(event) {
      if (event.target.matches(selectors.filter)) {
        this.updateFilters();
        return;
      }

      if (event.target.matches(selectors.quantityField)) {
        const row = event.target.closest(selectors.row);
        if (!row) return;
        event.target.value = this.normalizeQuantity(event.target.value, row);
        this.refreshSelection();
      }
    }

    onInput(event) {
      if (event.target.matches(selectors.quantityField)) {
        this.refreshSelection();
      }
    }

    getSelections() {
      return this.filters.map((filter) => ({
        index: filter.dataset.optionIndex,
        value: filter.value,
      }));
    }

    rowMatches(row, selections, ignoredIndex) {
      return selections.every((selection) => {
        if (!selection.value || selection.index === ignoredIndex) return true;
        return row.dataset['option' + selection.index] === selection.value;
      });
    }

    updateFilters() {
      const selections = this.getSelections();
      const hasActiveFilters = selections.some((selection) => selection.value);
      let matchingRows = 0;

      this.rows.forEach((row) => {
        const matches = this.rowMatches(row, selections);
        const withinCollapsedSet = !row.classList.contains(classes.extra) || this.expanded;
        const visible = matches && (hasActiveFilters || withinCollapsedSet);

        if (matches) matchingRows += 1;
        row.hidden = !visible;
      });

      this.updateFilterAvailability(selections);
      this.updateResults(matchingRows);

      if (this.clearButton) {
        this.clearButton.classList.toggle(classes.hidden, !hasActiveFilters);
      }

      if (this.toggleButton) {
        this.toggleButton.classList.toggle(classes.hidden, hasActiveFilters);
      }

      if (this.emptyElement) {
        this.emptyElement.classList.toggle(classes.hidden, matchingRows > 0);
      }
    }

    updateFilterAvailability(selections) {
      this.filters.forEach((filter) => {
        const filterIndex = filter.dataset.optionIndex;

        Array.from(filter.options).forEach((option) => {
          if (!option.value) {
            option.disabled = false;
            return;
          }

          const candidateSelections = selections.map((selection) => ({
            index: selection.index,
            value: selection.index === filterIndex ? option.value : selection.value,
          }));
          const isAvailable = this.rows.some((row) => this.rowMatches(row, candidateSelections));

          option.disabled = !isAvailable && option.value !== filter.value;
        });
      });
    }

    updateResults(count) {
      if (!this.resultsElement) return;

      const template = count === 1 ? this.dataset.resultOne : this.dataset.resultOther;
      this.resultsElement.textContent = template ? template.replace('[count]', count) : String(count);
    }

    clearFilters() {
      this.filters.forEach((filter) => {
        filter.value = '';
      });
      this.updateFilters();
      this.filters[0]?.focus();
    }

    toggleRows() {
      this.expanded = !this.expanded;
      this.toggleButton.setAttribute('aria-expanded', String(this.expanded));

      if (this.toggleText) {
        this.toggleText.textContent = this.expanded
          ? this.toggleButton.dataset.showFewerText
          : this.toggleButton.dataset.showAllText;
      }

      this.updateFilters();
    }

    getQuantityRules(row) {
      const minimum = Math.max(1, Number(row.dataset.quantityMin) || 1);
      const increment = Math.max(1, Number(row.dataset.quantityIncrement) || 1);
      const parsedMaximum = Number(row.dataset.quantityMax);
      let maximum = null;

      if (row.dataset.quantityMax && parsedMaximum >= minimum) {
        maximum = minimum + Math.floor((parsedMaximum - minimum) / increment) * increment;
      }

      return {minimum, increment, maximum};
    }

    normalizeQuantity(value, row) {
      let quantity = Math.max(0, Math.floor(Number(value) || 0));
      if (quantity === 0) return 0;

      const {minimum, increment, maximum} = this.getQuantityRules(row);
      quantity = Math.max(quantity, minimum);
      quantity = minimum + Math.floor((quantity - minimum) / increment) * increment;

      if (maximum !== null && quantity > maximum) {
        if (maximum < minimum) return 0;
        quantity = minimum + Math.floor((maximum - minimum) / increment) * increment;
      }

      return quantity;
    }

    changeQuantity(button, direction) {
      const row = button.closest(selectors.row);
      const field = row?.querySelector(selectors.quantityField);
      if (!row || !field || row.classList.contains(classes.unavailable)) return;

      const {minimum, increment, maximum} = this.getQuantityRules(row);
      const current = this.normalizeQuantity(field.value, row);
      let nextQuantity;

      if (direction > 0) {
        nextQuantity = current === 0 ? minimum : current + increment;
        if (maximum !== null) nextQuantity = Math.min(nextQuantity, maximum);
      } else {
        nextQuantity = current <= minimum ? 0 : current - increment;
      }

      field.value = this.normalizeQuantity(nextQuantity, row);
      field.dispatchEvent(new Event('change', {bubbles: true}));
    }

    normalizeAllQuantities() {
      this.rows.forEach((row) => {
        const field = row.querySelector(selectors.quantityField);
        if (field) field.value = this.normalizeQuantity(field.value, row);
      });
      this.refreshSelection();
    }

    getRowQuantity(row) {
      const field = row.querySelector(selectors.quantityField);
      return field ? Math.max(0, Math.floor(Number(field.value) || 0)) : 0;
    }

    refreshSelection() {
      let totalItems = 0;
      let subtotal = 0;

      this.rows.forEach((row) => {
        const quantity = this.getRowQuantity(row);
        const price = Number(row.dataset.price) || 0;
        const unavailable = row.classList.contains(classes.unavailable);
        const field = row.querySelector(selectors.quantityField);
        const minusButton = row.querySelector(selectors.minus);
        const plusButton = row.querySelector(selectors.plus);
        const {maximum} = this.getQuantityRules(row);

        row.classList.toggle(classes.selected, quantity > 0);
        totalItems += quantity;
        subtotal += quantity * price;

        if (field) field.disabled = unavailable || this.isSubmitting;
        if (minusButton) minusButton.disabled = unavailable || this.isSubmitting || quantity <= 0;
        if (plusButton) {
          plusButton.disabled = unavailable || this.isSubmitting || (maximum !== null && quantity >= maximum);
        }
      });

      if (this.totalElement) this.totalElement.textContent = totalItems;
      if (this.subtotalElement) this.subtotalElement.textContent = this.formatMoney(subtotal);
      if (this.summaryElement) this.summaryElement.classList.toggle(classes.active, totalItems > 0);
      if (this.submitButton) this.submitButton.disabled = totalItems <= 0 || this.isSubmitting;

      return totalItems;
    }

    formatMoney(cents) {
      if (window.theme && typeof window.theme.formatMoney === 'function') {
        return window.theme.formatMoney(cents, this.dataset.moneyFormat);
      }

      return (cents / 100).toFixed(2);
    }

    buildItemsPayload() {
      return this.rows
        .map((row) => ({
          id: Number(row.dataset.variantId),
          quantity: this.getRowQuantity(row),
        }))
        .filter((item) => item.quantity > 0);
    }

    setSubmitting(isSubmitting) {
      this.isSubmitting = isSubmitting;
      this.submitButton?.classList.toggle(classes.loading, isSubmitting);
      this.submitButton?.setAttribute('aria-busy', String(isSubmitting));
      this.refreshSelection();
    }

    hideError() {
      if (!this.errorElement) return;
      this.errorElement.textContent = '';
      this.errorElement.classList.add(classes.hidden);
    }

    showError(message) {
      if (!this.errorElement) return;
      this.errorElement.textContent = message || this.dataset.errorText;
      this.errorElement.classList.remove(classes.hidden);
    }

    resetRows() {
      this.rows.forEach((row) => {
        const field = row.querySelector(selectors.quantityField);
        if (field) field.value = 0;
      });
      this.refreshSelection();
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.isSubmitting) return;

      this.normalizeAllQuantities();
      const items = this.buildItemsPayload();
      if (!items.length) return;

      this.setSubmitting(true);
      this.hideError();

      try {
        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/json',
          },
          body: JSON.stringify({items}),
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || this.dataset.errorText);
        }

        this.resetRows();
        this.setSubmitting(false);

        if (document.querySelector('[data-cart-drawer]')) {
          document.dispatchEvent(new CustomEvent('theme:cart:refresh', {bubbles: true}));
        } else {
          window.location.assign(this.dataset.cartUrl);
        }
      } catch (error) {
        this.showError(error.message || this.dataset.errorText);
        this.setSubmitting(false);
      }
    }
  }

  customElements.define('bulk-order-form', BulkOrderForm);
})();
